// Line-count audit (2026-05-04): 105 lines.
import type { MutableRefObject } from 'react';
import type { PropertyMessage } from '@/components/ai/types';

export function getAgentThreadKey(projectId: string): string {
  return `terramatrix_agent_thread_${projectId}`;
}

export function getUserAgentThreadKey(projectId: string): string {
  return `terramatrix_user_agent_thread_${projectId}`;
}

export function isAgentSelectionContinuationInput(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) {
    return false;
  }

  return /^(yes|yeah|yep|sure|ok|okay|do it|sounds good|that works)\b/i.test(trimmed)
    || /(^#\d+\b|\b(option|number)\s*\d+\b|\bthe\s+(first|second|third)\s+one\b|\b(best|top)\s+one\b)/i.test(trimmed)
    || /\b(let'?s|lets)\s+(continue|go|do)\s+with\b|\b(continue|go|proceed|move)\s+with\b|\b(pick|choose|select|use)\b/i.test(trimmed);
}

export function getRecentAssistantMessages(messages: PropertyMessage[]): PropertyMessage[] {
  return [...messages]
    .reverse()
    .filter((message) => message.role === 'assistant')
    .slice(0, 6);
}

export function hasRecentUserAgentContext(
  messages: PropertyMessage[],
  userAgentThreadIdRef: MutableRefObject<string | null>
): boolean {
  return Boolean(userAgentThreadIdRef.current)
    || getRecentAssistantMessages(messages).some((message) => message.metadata?.source === 'user-agent');
}

export function isExplicitVisualEditorIntent(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) {
    return false;
  }

  return /^\//.test(trimmed)
    || /^(undo|redo|repeat)\b/i.test(trimmed)
    || /^(navigate|nav)\s+to\b|^go\s*to\b|^switch\s+to\s+page\b|^(open|show|view)\s+page\b/i.test(trimmed)
    || /\b(check|run|audit|test)\b.*\b(accessibility|a11y|wcag)\b/i.test(trimmed)
    || /\b(what data (do i have|is available|can i use)|show\s+(me\s+)?(available\s+)?(data|sources|schemas)|browse\s+data|explore\s+data(\s+sources)?|my data|data sources)\b/i.test(trimmed)
    || /\b(build|create|make|generate|design|populate|fill)\b.*\b(page|layout|dashboard|section|card|grid|form|canvas|home)\b/i.test(trimmed)
    || /\b(empty|blank)\b.*\b(page|canvas|home)\b/i.test(trimmed)
    || /\b(add|insert|place)\b.*\b(component|section|header|footer|sidebar|card|chart|table|button|text|image|container)\b/i.test(trimmed)
    || /\b(connect|bind|link)\b.*\b(chart|table|component|gauge|graph)\b.*\b(to|with)\b.*\b(data|schema|sensor|source)\b/i.test(trimmed)
    || /\b(change|update|modify|adjust)\b.*\b(color|font|style|background|border|padding|margin)\b/i.test(trimmed);
}

export function isVagueUserAgentContinuationInput(input: string): boolean {
  const trimmed = input.trim();
  if (!trimmed) {
    return false;
  }

  return /\b(let'?s|lets)\s+(work on|continue|keep going|move forward|start on)\b/i.test(trimmed)
    || /\b(work on|continue|keep going|move forward)\b.*\b(details?|that|this|it)\b/i.test(trimmed)
    || /\b(what next|next steps?|go ahead|sounds good|that works|help me with that|can you help with that)\b/i.test(trimmed);
}

function isUserAgentRecommendationMessage(message?: PropertyMessage): boolean {
  if (!message || message.role !== 'assistant') {
    return false;
  }

  const content = message.content || '';
  const hasRecommendationLanguage = /\b(top recommendations?|recommend(?:ed|ation)?|best:|want me to build one|reply ['"]?yes['"]?|scope:\s*['"]?user['"]?)/i.test(content);
  const hasAgentOptions = /\bagent\b/i.test(content) && /(?:^|\n)\s*(?:\*\*)?(?:best|1|2|3)[\.:]/im.test(content);
  const fromUserAgent = message.metadata?.source === 'user-agent';

  return fromUserAgent || hasRecommendationLanguage || hasAgentOptions;
}

export function shouldPreferUserAgentContinuation(
  input: string,
  messages: PropertyMessage[],
  userAgentThreadIdRef: MutableRefObject<string | null>
): boolean {
  const hasAgentContext = hasRecentUserAgentContext(messages, userAgentThreadIdRef);
  if (!hasAgentContext) {
    return false;
  }

  if (isAgentSelectionContinuationInput(input)) {
    return true;
  }

  if (isExplicitVisualEditorIntent(input)) {
    return false;
  }

  if (isVagueUserAgentContinuationInput(input)) {
    return true;
  }

  const recentAssistantMessages = getRecentAssistantMessages(messages);
  return recentAssistantMessages.some(isUserAgentRecommendationMessage) && !isExplicitVisualEditorIntent(input);
}
